/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { CaseService } from 'app/cm/services/case-service';
import * as CM from 'app/cm/models/essette-api';
import { EssModalCustomElement } from 'app/shared/elements/ess-dialog';
import { SaveAcuityCustomElement } from 'app/cm/admin/acuity/save-acuity';
import { deepClone } from 'app/shared/lib/utilities';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { Toaster } from 'app/shared/lib/toaster';
import { validate } from 'app/shared/elements/ess-rule';

@autoinject()
export class Acuities extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected acuities: ReadonlyArray<CM.Acuity> = [];

    private sorting = new Sorting();
    protected resultCount = 0;
    protected pageSize = 25;
    protected includeInactive = false;
    protected editedAcuity?: CM.Acuity;
    protected isValid: boolean = false;
    protected validate = validate;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $modal!: EssModalCustomElement;
    protected $form!: IForm;
    protected $saveAcuity!: SaveAcuityCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CaseService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);

        // Default Sort
        this.sorting.entries = [
            {
                reference: 'value',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
            if(this.sorting.entries.length == 0) {
                this.sorting.entries = [
                    {
                        reference: 'value',
                        direction: SortDirection.Ascending
                    }
                ];
            }
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }

        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    private editAcuity(acuity: CM.Acuity): void {
        if(!acuity || !this.$modal) {
            return;
        }

        this.$saveAcuity.acuity = deepClone(acuity);
        this.$modal.open();
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadAcuities();
    }

    protected addAcuity() {
        this.$modal.open();
    }

    protected modalClose(): void {
        if(this.$form.reset) {
            this.$form.reset();
        }
        this.$saveAcuity.reset();
        this.$modal.close();
    }

    protected async handleSaveAcuityFormSuccess(): Promise<void> {
        if(this.$saveAcuity.reset) {
            this.$saveAcuity.reset();
        }
        this.$modal.close();
    }

    public async submit(): Promise<boolean> {
        const response = await this.$saveAcuity.saveAcuity();
        if(this.$form.reset) {
            this.$form.reset();
        }
        this.$modal.close();
        this.reload();
        return response;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadAcuities(): Promise<void> {
        const includes: string[] = [];
        const options: Partial<IQueryLoadOptions> = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: this.includeInactive
        };

        try {
            this.state.beginLoad();

            const response = await this.service.loadAcuities(options, includes);
            this.acuities = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateAcuity(acuity: CM.Acuity, isActive: boolean) {
        if(!isActive) {
            const result = await confirmation.showDeactivate('cm:acuity.$');
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateAcuity(acuity.id!, isActive);
            isSuccessful = response.isSuccessful;
            if(response.isSuccessful) {
                this.loadAcuities();
            }
        }
        catch(error) {
            this.toaster.standard.activate('cm:acuity.$', isActive, isSuccessful);
            throw error;
        } finally {
            this.toaster.standard.activate('cm:acuity.$', isActive, isSuccessful);
            this.state.endSave();
        }
    }

}
